// @ts-nocheck
// ============================================================
// BIKE FIT TRANSFER — CORE ALGORITHM
// Version: 2.0 (biomechanics-correct rewrite)
//
// APPROACH:
//   Rather than accumulating geometry deltas, we solve absolute
//   2D coordinates for the bar position on the current bike, then
//   back-calculate the stem length and spacers required to hit
//   that same absolute position on the new bike.
//
// COORDINATE SYSTEM:
//   Origin = centre of bottom bracket
//   X = horizontal, positive = forward (toward front wheel)
//   Y = vertical, positive = up
//
//   Stack = Y distance from BB to top of head tube
//   Reach = X distance from BB to top of head tube
//   These are the published frame geometry values.
//
// BIOMECHANICAL ANCHORS (never violated):
//   1. Saddle height is preserved (only adjusted for crank length change)
//   2. Saddle setback is preserved (only adjusted for STA change)
//   3. Saddle position is NEVER used to compensate for reach problems
//   4. Crank length change only affects saddle height
//
// NOTES ON SIGN CONVENTIONS:
//   stemAngle: negative = angled downward (typical road stem, e.g. -6°)
//              positive = angled upward (flipped stem, e.g. +6°)
//              must be in range (-89°, +89°) — function returns an error object otherwise
//   saddleSetback: negative = saddle tip is forward of BB plumb line
//     (almost all road riders are negative, e.g. -96mm)
//   spacers: always >= 0
//
// BAR DROP / BAR REACH DEFAULTS:
//   When the user hasn't entered bar measurements (Advanced Options off),
//   the caller should pass explicit defaults rather than relying on this
//   function to assume values. Recommended defaults if not collected:
//     barDrop = 125mm, barReach = 80mm (typical mid-range road bar)
//   These values cancel out when current and new bars are the same,
//   so the default only matters when Advanced Options are enabled and
//   the user changes bar profile. The UI should make this explicit.
// ============================================================

export function calculateFitTransfer({
  curGeo, // { stack, reach, seatTubeAngle }
  newGeo, // { stack, reach, seatTubeAngle }
  saddleHeight, // mm, vertical BB to saddle top
  saddleSetback, // mm, negative = forward of BB
  stemLength, // mm
  stemAngle, // degrees, negative = angled down
  spacers, // mm, total spacers under stem
  newStemAngle, // degrees, negative = angled down
  barDrop, // mm, vertical drop from bar clamp to drops
  barReach, // mm, horizontal reach from bar clamp to drops
  newBarDrop, // mm
  newBarReach, // mm
  crankLength, // mm
  newCrankLength, // mm
}) {
  const toRad = (d) => (d * Math.PI) / 180;

  const H = parseFloat(saddleHeight);
  const S = parseFloat(saddleSetback);
  const stemL = parseFloat(stemLength);
  const stemA = toRad(parseFloat(stemAngle));
  const newStemA = toRad(parseFloat(newStemAngle));
  const sp = parseFloat(spacers);
  const bDrop = parseFloat(barDrop);
  const bReach = parseFloat(barReach);
  const newBDrop = parseFloat(newBarDrop);
  const newBReach = parseFloat(newBarReach);
  const crank = parseFloat(crankLength);
  const newCrank = parseFloat(newCrankLength);

  // ─────────────────────────────────────────────────────────
  // GUARD: Stem angle validation
  //
  // Stem angles outside ±90° cause cos(stemA) to go negative,
  // which would invert the X solve and produce nonsense output.
  // Typical road stems: -6° to -17°. Flipped stem: +6° to +17°.
  // We guard at ±80° to leave a safe margin.
  // ─────────────────────────────────────────────────────────
  const stemAngleDeg = parseFloat(stemAngle);
  const newStemAngleDeg = parseFloat(newStemAngle);

  if (Math.abs(stemAngleDeg) >= 90 || Math.abs(newStemAngleDeg) >= 90) {
    return {
      error: "STEM_ANGLE_OUT_OF_RANGE",
      message:
        "Stem angle must be between -89° and +89°. Values at or beyond ±90° are not physically meaningful for this calculation.",
    };
  }

  // ─────────────────────────────────────────────────────────
  // STEP 1 — SADDLE SETBACK CORRECTION FOR STA CHANGE
  //
  // The seatpost sits along the seat tube. If STA changes,
  // the same seatpost extension places the saddle at a
  // different horizontal position relative to BB.
  //
  // We compute the seatpost extension (hypotenuse) from the
  // current bike's STA and saddle height, then find how far
  // forward the saddle tip sits at that extension on each STA.
  //
  // Note: saddle height H is the vertical component, so:
  //   ext = H / sin(STA)
  //   horizontal component from BB along seat tube = ext * cos(STA)
  //
  // staShift > 0 means saddle moves rearward on new bike
  // (fitter must slide saddle forward to compensate)
  // ─────────────────────────────────────────────────────────
  const oldSTA = curGeo.seatTubeAngle;
  const newSTA = newGeo.seatTubeAngle;

  const ext = H / Math.sin(toRad(oldSTA));
  const oldX = ext * Math.cos(toRad(oldSTA));
  const newX = ext * Math.cos(toRad(newSTA));

  // staShift: how much the saddle horizontal position changes
  // due to STA difference alone, at the same seatpost extension.
  // Positive = saddle moves rearward relative to BB on new frame.
  const staShift = newX - oldX;

  // To preserve the rider's biomechanical setback position,
  // compensate: if STA pushes saddle rearward, slide it forward.
  const newSetback = S - staShift;

  // ─────────────────────────────────────────────────────────
  // STEP 2 — CRANK LENGTH → SADDLE HEIGHT ADJUSTMENT
  //
  // A longer crank increases effective leg extension at bottom
  // of stroke. To maintain the same knee angle, saddle must
  // come down by exactly the crank length difference.
  // ─────────────────────────────────────────────────────────
  const crankDiff = newCrank - crank;
  const newSaddleHeight = H - crankDiff;

  // ─────────────────────────────────────────────────────────
  // STEP 3 — ABSOLUTE BAR POSITION ON CURRENT BIKE
  //
  // We compute the (X, Y) position of the bar drops in the
  // BB-origin coordinate system.
  //
  // The head tube top is at (reach, stack).
  // Add spacers vertically (spacers are always vertical).
  // Add stem contribution: stem runs at stemAngle from horizontal.
  // Add bar: reach is horizontal forward, drop is vertical down.
  //
  // Note on stem angle sign:
  //   stemA is negative for a downward-angled stem (typical road)
  //   cos(negative) = positive → adds forward reach ✓
  //   sin(negative) = negative → subtracts height ✓
  // ─────────────────────────────────────────────────────────
  const barX_cur = curGeo.reach + stemL * Math.cos(stemA) + bReach;

  const barY_cur = curGeo.stack + sp + stemL * Math.sin(stemA) - bDrop;

  // ─────────────────────────────────────────────────────────
  // STEP 4 — SOLVE NEW STEM LENGTH
  //
  // Target: reproduce (barX_cur, barY_cur) on new bike.
  //
  // The bar drops on the new bike sit at:
  //   X = newGeo.reach + newStemL * cos(newStemA) + newBReach
  //   Y = newGeo.stack + newSpacers + newStemL * sin(newStemA) - newBDrop
  //
  // From the X equation (spacers don't affect X):
  //   newStemL * cos(newStemA) = targetX - newGeo.reach - newBReach
  //   newStemL = deltaX / cos(newStemA)
  //
  // This is the correct solve. The Pythagorean approach
  // (sqrt(deltaX² + deltaY²)) would implicitly solve for both
  // length AND angle, but angle is already fixed as an input.
  // ─────────────────────────────────────────────────────────
  const targetX = barX_cur;
  const targetY = barY_cur;

  const deltaX = targetX - (newGeo.reach + newBReach);

  const newStemLength = deltaX / Math.cos(newStemA);

  // Guard: negative stem length means the new frame's reach + bar reach
  // already overshoots the target X even with a 0mm stem — a fundamental
  // incompatibility. Return an error object rather than a nonsense value.
  if (newStemLength < 0) {
    return {
      error: "NEGATIVE_STEM_LENGTH",
      message: `The new frame's reach (${newGeo.reach}mm) already overshoots your target bar position. Even a 0mm stem would place the bars too far forward. Try a smaller frame size or a frame with less reach.`,
      barX_cur: Math.round(barX_cur),
      barY_cur: Math.round(barY_cur),
    };
  }

  // ─────────────────────────────────────────────────────────
  // STEP 5 — SOLVE NEW SPACERS FROM Y EQUATION
  //
  //   targetY = newGeo.stack + newSpacers + newStemL * sin(newStemA) - newBDrop
  //   newSpacers = targetY - newGeo.stack - newStemL * sin(newStemA) + newBDrop
  // ─────────────────────────────────────────────────────────
  const newSpacers =
    targetY - newGeo.stack - newStemLength * Math.sin(newStemA) + newBDrop;

  // ─────────────────────────────────────────────────────────
  // STEP 6 — SEATPOST OFFSET RECOMMENDATION
  //
  // Based on final corrected setback value.
  // Negative = saddle tip forward of BB (normal for road).
  // ─────────────────────────────────────────────────────────
  let seatpostRec, seatpostReason;
  if (newSetback <= -60) {
    seatpostRec = "0mm offset";
    seatpostReason = "Forward position — 0mm offset maximises rail range";
  } else if (newSetback <= -30) {
    seatpostRec = "0mm offset";
    seatpostReason = "Moderate forward position — 0mm offset preferred";
  } else if (newSetback >= 20) {
    seatpostRec = "25mm offset";
    seatpostReason = "Rearward position requires 25mm offset";
  } else {
    seatpostRec = "0mm or 25mm offset";
    seatpostReason = "Borderline — try 0mm first, adjust rail accordingly";
  }

  // ─────────────────────────────────────────────────────────
  // STEP 7 — WARNINGS
  //
  // Collected as objects so the UI can render them with
  // appropriate colour coding. Ordered by severity.
  // ─────────────────────────────────────────────────────────
  const warnings = [];

  // Stem length out of range
  if (newStemLength < 70 || newStemLength > 140) {
    warnings.push({
      level: "red",
      param: "stem",
      msg: `Required stem (${Math.round(
        newStemLength
      )}mm) is outside the normal 70–140mm range. This bike may not fit you well in this size.`,
    });
  } else if (newStemLength < 80 || newStemLength > 120) {
    warnings.push({
      level: "amber",
      param: "stem",
      msg: `Stem length (${Math.round(
        newStemLength
      )}mm) is at the limits of typical fit range. Consider whether a different frame size would be a better match.`,
    });
  }

  // Spacers out of range
  if (newSpacers < 0) {
    warnings.push({
      level: "red",
      param: "spacers",
      msg: `This setup requires negative spacers (${Math.round(
        newSpacers
      )}mm) — the bar position cannot be replicated on this frame without flipping the stem or cutting the steerer.`,
    });
  } else if (newSpacers > 30) {
    warnings.push({
      level: "red",
      param: "spacers",
      msg: `Required spacer stack (${Math.round(
        newSpacers
      )}mm) exceeds typical safety limits. Check your fork's maximum steerer spacer recommendation.`,
    });
  } else if (newSpacers > 20) {
    warnings.push({
      level: "amber",
      param: "spacers",
      msg: `Spacer stack (${Math.round(
        newSpacers
      )}mm) is high. Achievable, but check fork steerer length and manufacturer limits.`,
    });
  }

  // Large STA-driven setback change
  if (Math.abs(staShift) > 10) {
    warnings.push({
      level: "amber",
      param: "setback",
      msg: `The seat tube angle difference moves the saddle ${Math.round(
        Math.abs(staShift)
      )}mm ${
        staShift > 0 ? "rearward" : "forward"
      }. Verify the saddle rail has enough travel to accommodate this.`,
    });
  }

  // Crank length change is large
  if (Math.abs(crankDiff) >= 5) {
    warnings.push({
      level: "amber",
      param: "saddle",
      msg: `Crank length changes by ${Math.abs(
        crankDiff
      )}mm, requiring a ${Math.abs(
        crankDiff
      )}mm saddle height adjustment. This is significant — recheck your position after the first ride.`,
    });
  }

  // ─────────────────────────────────────────────────────────
  // STEP 8 — EXPLANATION STRINGS
  //
  // Human-readable, biomechanically grounded explanations
  // for each output value. Used in the "what changed" section.
  // ─────────────────────────────────────────────────────────
  const explanations = {
    saddleHeight:
      crankDiff !== 0
        ? `Adjusted ${Math.abs(crankDiff)}mm ${
            crankDiff > 0 ? "down" : "up"
          } because your new cranks are ${Math.abs(crankDiff)}mm ${
            crankDiff > 0 ? "longer" : "shorter"
          }.`
        : `Unchanged — crank length is the same.`,

    setback:
      staShift !== 0
        ? `The new frame's seat tube angle (${newSTA}° vs ${oldSTA}°) shifts the saddle ${Math.round(
            Math.abs(staShift)
          )}mm ${
            staShift > 0 ? "rearward" : "forward"
          } at the same seatpost extension. Setback corrected to compensate.`
        : `Unchanged — seat tube angles are identical.`,

    stemLength:
      `Solved by matching your exact bar position from the current bike. ` +
      `New frame reach is ${newGeo.reach}mm vs ${curGeo.reach}mm on your current bike.` +
      (Math.abs(newBReach - bReach) > 0
        ? ` Bar reach changes by ${newBReach - bReach}mm.`
        : ""),

    spacers:
      `Solved from the vertical bar coordinate. ` +
      `New frame stack is ${newGeo.stack}mm vs ${curGeo.stack}mm on your current bike.` +
      (Math.abs(newBDrop - bDrop) > 0
        ? ` Bar drop changes by ${newBDrop - bDrop}mm.`
        : ""),
  };

  // ─────────────────────────────────────────────────────────
  // OUTPUT
  // ─────────────────────────────────────────────────────────
  return {
    // Primary outputs (rounded for display)
    newSaddleHeight: Math.round(newSaddleHeight * 10) / 10,
    newSetback: Math.round(newSetback),
    newStemLength: Math.round(newStemLength),
    newSpacers: Math.round(newSpacers),

    // For display in result cards
    seatpostRec,
    seatpostReason,

    // Debug / breakdown values (shown in "what changed" section)
    staShift: Math.round(staShift * 10) / 10,
    crankDiff: Math.round(crankDiff * 10) / 10,
    oldSTA,
    newSTA,
    barX_cur: Math.round(barX_cur),
    barY_cur: Math.round(barY_cur),

    // Explanations for each result card
    explanations,

    // Warnings array — render with level-appropriate colours
    warnings,
  };
}
